import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * Cloudflare Turnstile bot check. Most visitors pass without a puzzle.
 * The token it produces is checked by the CMS; each token works once, so call reset() after submitting.
 */

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise;
function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile unavailable')));
            script.onerror = () => {
                scriptPromise = undefined;
                reject(new Error('Turnstile could not be loaded'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget({ onToken, onError }, ref) {
    const container = useRef(null);
    const widgetId = useRef(null);
    const callbacks = useRef({ onToken, onError });
    callbacks.current = { onToken, onError };

    useImperativeHandle(ref, () => ({
        reset() {
            callbacks.current.onToken('');
            if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
        },
    }));

    useEffect(() => {
        let active = true;
        loadTurnstile()
            .then((turnstile) => {
                if (!active || !container.current) return;
                widgetId.current = turnstile.render(container.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => callbacks.current.onToken(token),
                    'expired-callback': () => callbacks.current.onToken(''),
                    'error-callback': () => {
                        callbacks.current.onToken('');
                        if (callbacks.current.onError) callbacks.current.onError();
                    },
                });
            })
            .catch(() => active && callbacks.current.onError && callbacks.current.onError());
        return () => {
            active = false;
            if (window.turnstile && widgetId.current !== null) window.turnstile.remove(widgetId.current);
            widgetId.current = null;
        };
    }, []);

    return <div ref={container} />;
});

export default TurnstileWidget;
